import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["tasks"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      taskActivityList: build.query<
        TaskActivityListApiResponse,
        TaskActivityListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/tasks/${queryArg.taskId}/activity/`,
        }),
        providesTags: ["tasks"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type TaskActivityListApiResponse = /** status 200  */ TaskActivity[];
export type TaskActivityListApiArg = {
  matterId: string;
  taskId: string;
};
export type PolicyType =
  | "AUTO"
  | "HOMEOWNERS"
  | "COMMERCIAL"
  | "HEALTH"
  | "UMBRELLA"
  | "WORKERS_COMPENSATION"
  | "GENERAL_LIABILITY"
  | "OTHER";
export type ContactHandleType =
  | "PHONE"
  | "EMAIL"
  | "WEBSITE"
  | "FAX"
  | "OTHER"
  | "MAILING_ADDRESS";
export type WebSource = {
  /** The URL of the source */
  url: string;
  /** Very short display text for the source URL */
  title: string;
};
export type ContactHandleSchema = {
  id?: (string | string) | null;
  /** The captured contact information. Should contain an E.164 formatted phone number, or an email address, or a website, or a fax number. */
  value?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  handle_type: ContactHandleType;
  /** Short description of the handle; e.g. 'Billing Dept Toll-Free', 'Front Desk Fax' etc. */
  name?: string | null;
  /** Optional notes about the handle. */
  notes?: string | null;
  /** Citations for the handle. */
  sources?: WebSource[] | null;
  /** Whether this is the preferred contact handle. */
  preferred?: boolean | null;
  division_id?: (string | string) | null;
};
export type CarrierDetail = {
  /**
            The colloquial, common form, well-known name of the insurance carrier if mentioned, null otherwise.
            This is a key field for matching and deduplication.
             */
  name?: string | null;
  /**
            The BEST (at _MOST_ 2 handles per type) web pages or phone numbers for submitting and managing claims if mentioned, null otherwise.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            The headquarters address of the insurance carrier if mentioned, null otherwise.
             */
  address?: string | null;
};
export type IdentifierType =
  | "POLICY_NUMBER"
  | "MEMBER_ID"
  | "GROUP_NUMBER"
  | "OTHER";
export type PolicyIdentifier = {
  /**
            The type of identifier for the policy.
             */
  identifier_type: IdentifierType;
  /**
            The value of the identifier for the policy.
             */
  identifier_value: string;
};
export type BenefitType =
  | "PERSONAL_INJURY_PROTECTION"
  | "MEDICAL_PAYMENTS"
  | "UNINSURED_MOTORIST"
  | "UNDER_INSURED_MOTORIST"
  | "BODILY_INJURY_LIABILITY"
  | "PROPERTY_DAMAGE_LIABILITY"
  | "COMPREHENSIVE_AUTO"
  | "COLLISION_AUTO"
  | "WORKERS_COMPENSATION"
  | "OTHER";
export type BenefitDetail = {
  /**
            A limited categorization of insurance benefits we are interested in extracting.
            
            <benefits-type-glossary>
                PIP == PERSONAL_INJURY_PROTECTION
                MEDPAY == MEDICAL_PAYMENTS
                UM == UNINSURED_MOTORIST
                UIM == UNDER_INSURED_MOTORIST
                BI == BODILY_INJURY_LIABILITY
                COMPREHENSIVE == COMPREHENSIVE_AUTO
                COLLISION == COLLISION_AUTO
                anything else == OTHER
            </benefits-type-glossary>
              
             */
  benefit_type?: BenefitType;
  /**
            Structured map of coverage limitation labels to their numeric dollar amounts.
            Keys describe the limit type (e.g., "per person", "per accident", "bodily injury per person").
            Values MUST be integers representing dollar amounts (e.g., 25000 for $25,000).
    
            IMPORTANT: Do NOT use slash notation like "25000/50000". Instead, create SEPARATE entries:
            - WRONG: {"bodily injury": "25000/50000"}
            - CORRECT: {"bodily injury per person": 25000, "bodily injury per accident": 50000}
    
            Examples:
            - {"per person": 100000, "per accident": 300000}
            - {"BI per person": 25000, "BI per accident": 50000, "PD": 25000}
            - {"medical payments": 5000}
             */
  limitations?: {
    [key: string]: number;
  } | null;
  /**
            Whether stacking benefits apply for UM/UIM coverage. When true, coverage limits from
            multiple policies can be combined. Only applicable for UNINSURED_MOTORIST and
            UNDER_INSURED_MOTORIST benefit types. Null indicates unknown/not specified.
             */
  is_stacked?: boolean | null;
};
export type CoverageDataSource =
  | "UNKNOWN"
  | "BPO"
  | "USER_EDIT"
  | "SYSTEM"
  | "VOICE_AI";
export type CoverageDataSourceInfo = {
  /** The source that created or last modified this data. */
  source?: CoverageDataSource;
  /** User ID if source is USER_EDIT. */
  user_id?: string | null;
  /** User display name if source is USER_EDIT (for display purposes). */
  user_name?: string | null;
  /** When the data was added/modified from this source. */
  timestamp?: string | null;
};
export type AdjusterDetail = {
  /**
            The benefit the adjuster is specifically assigned to, if explicitly stated, otherwise null.
             */
  benefit_type?: BenefitType;
  /**
            Name of the adjuster handling a related claim if explicitly stated, otherwise null.
             */
  name?: string | null;
  /**
            The BEST (at MOST 2 handles per type) contact methods for the adjuster (phone, email, fax) if explicitly stated,
            otherwise null.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            Metadata about the source of this adjuster data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo;
};
export type ClaimDetail = {
  /**
            The benefit against which the claim is being made, if explicitly stated, otherwise null.
            
            <benefits-type-glossary>
                PIP == PERSONAL_INJURY_PROTECTION
                MEDPAY == MEDICAL_PAYMENTS
                UM == UNINSURED_MOTORIST
                UIM == UNDER_INSURED_MOTORIST
                BI == BODILY_INJURY_LIABILITY
                COMPREHENSIVE == COMPREHENSIVE_AUTO
                COLLISION == COLLISION_AUTO
                anything else == OTHER
            </benefits-type-glossary>
              
             */
  benefit_type?: BenefitType;
  /**
            The claim identifier (number, id, etc.) for the policy, if explicitly stated, otherwise null.
             */
  claim_identifier?: string | null;
  /**
            Metadata about the source of this claim data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo;
};
export type VehicleDetail = {
  /**
            The make of the vehicle if explicitly stated, otherwise null.
             */
  make?: string | null;
  /**
            The model of the vehicle if explicitly stated, otherwise null.
             */
  model?: string | null;
  /**
            The year of the vehicle if explicitly stated, otherwise null.
             */
  year?: number | null;
  /**
            The VIN of the vehicle if explicitly stated, otherwise null.
             */
  vin?: string | null;
  /**
            The license plate of the vehicle if explicitly stated, otherwise null.
             */
  license_plate?: string | null;
  /**
            The color of the vehicle if explicitly stated, otherwise null.
             */
  color?: string | null;
};
export type PolicyDetail = {
  /**
            A well formatted label for the policy detail, i.e "VIN", "Vehicle Description", "Premium", "Insured Address", "Garage Address"
             */
  key: string;
  /**
            The value of the policy detail, i.e "1234567890", "Toyota Camry", "100000.00"
             */
  value: string;
};
export type SubrogatorDetail = {
  /**
            Name of the subrogator handling a related claim if explicitly stated, otherwise null.
             */
  name?: string | null;
  /**
            The BEST (at MOST 2 handles per type) contact methods for the subrogator (phone, email, fax) if explicitly stated,
            otherwise null.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            List of sources that provided the subrogator information if explicitly stated, otherwise null.
             */
  sources?: WebSource[] | null;
};
export type PropertyContactType =
  | "PROPERTY_OWNER"
  | "PROPERTY_MANAGER"
  | "OTHER";
export type PropertyContact = {
  /** The type of contact (owner, manager, or other). */
  contact_type: PropertyContactType;
  /** Custom label when contact_type is OTHER (e.g., 'Tenant', 'Security Company'). */
  custom_type_label?: string | null;
  /** Name of the contact person or entity. */
  name?: string | null;
  /** Contact methods: phone, email, fax, etc. */
  handles?: ContactHandleSchema[] | null;
  /** Mailing address for the contact. */
  address?: string | null;
  /** Additional notes about this contact. */
  notes?: string | null;
};
export type CoverageStatus =
  | "NEEDS_APPROVAL"
  | "APPROVED"
  | "REJECTED"
  | "NOT_APPLICABLE";
export type CoverageLiabilityStatus =
  | "ACCEPTED"
  | "REJECTED"
  | "PARTIALLY_ACCEPTED"
  | "LIABILITY_UNKNOWN"
  | "UNDER_INVESTIGATION";
export type CoverageCategory =
  | "FIRST_PARTY"
  | "FIRST_PARTY_HEALTH"
  | "THIRD_PARTY"
  | "NOT_LIABLE_INVOLVED_PARTY"
  | "UNCLEAR"
  | "PROPERTY"
  | "WORKERS_COMPENSATION";
export type ArtifactType =
  | "TRANSCRIPT"
  | "AUDIO_CONVERSATION"
  | "DOCUMENT"
  | "EMAIL"
  | "EMAIL_ATTACHMENT"
  | "INTAKE_NOTE"
  | "MESSAGE"
  | "MESSAGE_ATTACHMENT"
  | "CONVERSATION"
  | "BLOB"
  | "STAFF_NOTE";
export type CoverageMentionStubSchema = {
  id: string | string;
  created_at: string;
  artifact_id: string | string;
  artifact_type: ArtifactType;
  citation_ids?: string[] | null;
  confidence?: number | null;
  context?: string | null;
};
export type EnrichmentSource =
  | "perplexity"
  | "cached"
  | "manual"
  | "intake"
  | "extraction";
export type EnrichmentMetadata = {
  source_matter_id?: string | null;
  source_matter_name?: string | null;
  match_reason?: string | null;
  cached_at?: string | null;
  confidence_score?: number | null;
  [key: string]: any;
};
export type LorUploadedArtifactSummary = {
  id: string | string;
  blob_id?: (string | string) | null;
  name?: string | null;
  original_filename?: string | null;
  file_extension?: string | null;
  file_mime_type?: string | null;
  created_at?: string | null;
};
export type OpenClaimFieldStatus =
  | "VERIFIED"
  | "SUGGESTED"
  | "MISSING"
  | "UNKNOWN"
  | "WRONG";
export type OpenClaimCitedFieldStr = {
  /** The discovered or existing value for this field. */
  value?: string | null;
  /** The verification status of this field. */
  status?: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value. */
  confidence?: number | null;
  /** Citation IDs linking to source text in documents. */
  citation_ids?: string[];
  /** Artifact ID of the primary source document. */
  source_artifact_id?: (string | string) | null;
  /** Brief explanation of why this value was suggested or verified. */
  reasoning?: string | null;
};
export type OpenClaimClientInfo = {
  /** Client's full legal name. */
  name?: OpenClaimCitedFieldStr;
  /** Client's contact phone number. */
  phone?: OpenClaimCitedFieldStr;
  /** Client's email address. */
  email?: OpenClaimCitedFieldStr;
  /** Client's current mailing address (street address). */
  address?: OpenClaimCitedFieldStr;
  /** Client's city. */
  city?: OpenClaimCitedFieldStr;
  /** Client's ZIP code. */
  zip_code?: OpenClaimCitedFieldStr;
  /** Client's date of birth (MM/DD/YYYY). */
  dob?: OpenClaimCitedFieldStr;
  /** Last four digits of client's Social Security Number. */
  last_four_ssn?: OpenClaimCitedFieldStr;
};
export type OpenClaimIncidentInfo = {
  /** Date the incident occurred (MM/DD/YYYY). */
  date_of_loss?: OpenClaimCitedFieldStr;
  /** Time the incident occurred (e.g., 2:30 PM or 14:30). */
  time_of_loss?: OpenClaimCitedFieldStr;
  /** Location where the incident occurred (street address or intersection). */
  location?: OpenClaimCitedFieldStr;
  /** Weather conditions at time of incident. */
  weather_conditions?: OpenClaimCitedFieldStr;
  /** Brief description of how the incident occurred. */
  incident_description?: OpenClaimCitedFieldStr;
};
export type OpenClaimCitedFieldUnionIntNoneType = {
  /** The discovered or existing value for this field. */
  value?: number | null;
  /** The verification status of this field. */
  status?: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value. */
  confidence?: number | null;
  /** Citation IDs linking to source text in documents. */
  citation_ids?: string[];
  /** Artifact ID of the primary source document. */
  source_artifact_id?: (string | string) | null;
  /** Brief explanation of why this value was suggested or verified. */
  reasoning?: string | null;
};
export type OpenClaimVehicleInfo = {
  /** Vehicle model year. */
  year?: OpenClaimCitedFieldUnionIntNoneType;
  /** Vehicle manufacturer (e.g., Toyota, Ford). */
  make?: OpenClaimCitedFieldStr;
  /** Vehicle model name (e.g., Camry, F-150). */
  model?: OpenClaimCitedFieldStr;
  /** Vehicle Identification Number. */
  vin?: OpenClaimCitedFieldStr;
  /** License plate number and state. */
  license_plate?: OpenClaimCitedFieldStr;
  /** Vehicle color. */
  color?: OpenClaimCitedFieldStr;
  /** UUID of the coverage this vehicle should be assigned to. */
  suggested_coverage_id?: OpenClaimCitedFieldStr;
};
export type OpenClaimCoverageInfo = {
  /** Name of the insurance carrier. */
  carrier_name?: OpenClaimCitedFieldStr;
  /** Policy number on the insurance card/documents. */
  policy_number?: OpenClaimCitedFieldStr;
  /** Claims phone number for the carrier. */
  carrier_phone?: OpenClaimCitedFieldStr;
  /** Name of the policyholder/named insured. */
  named_insured?: OpenClaimCitedFieldStr;
  /** Existing claim number if a claim has already been filed. */
  claim_number?: OpenClaimCitedFieldStr;
};
export type OpenClaimAtFaultDriverInfo = {
  /** Name of the at-fault or other involved driver. */
  name?: OpenClaimCitedFieldStr;
  /** Phone number of the at-fault driver. */
  phone?: OpenClaimCitedFieldStr;
  /** Address of the at-fault driver. */
  address?: OpenClaimCitedFieldStr;
};
export type OpenClaimContextSchema = {
  /** Client/claimant information. */
  client?: OpenClaimClientInfo;
  /** Details about the incident/accident. */
  incident?: OpenClaimIncidentInfo;
  /** Client's vehicle(s) involved in the incident. */
  client_vehicles?: OpenClaimVehicleInfo[];
  /** Insurance coverage details. */
  coverage?: OpenClaimCoverageInfo;
  /** At-fault or other party driver information. */
  at_fault_driver?: OpenClaimAtFaultDriverInfo;
  /** At-fault or other party vehicle information. */
  defendant_vehicle?: OpenClaimVehicleInfo;
  /** ISO timestamp when AI discovery completed. */
  discovery_completed_at?: string | null;
  /** Summary of what was found and what's missing. */
  discovery_summary?: string | null;
};
export type CoverageSchema = {
  /**
            The type of policy if explicitly stated, otherwise null.
             */
  policy_type?: PolicyType;
  /**
            Details about the insurance carrier if mentioned, otherwise null.
             */
  carrier?: CarrierDetail;
  /**
            Carrier-issued identifiers for the policy, if explicitly stated, otherwise null.
             */
  policy_identifiers?: PolicyIdentifier[] | null;
  /**
            List of names of primary policyholders or insured entities if stated or implied, otherwise null.
             */
  named_insured?: string[] | null;
  /**
            List of benefits covered by the policy, if explicitly stated, otherwise null.
             */
  benefits?: BenefitDetail[] | null;
  /**
            Any known adjusters handling claims against this policy, if explicitly stated, otherwise null.
             */
  adjusters?: AdjusterDetail[] | null;
  /**
            Any known claims opened against the policy, if explicitly stated, otherwise null.
             */
  claims?: ClaimDetail[] | null;
  /**
            List of vehicles covered by the policy, if applicable and explicitly stated, otherwise null.
             */
  vehicles?: VehicleDetail[] | null;
  /**
            Structured list of extracted policy details, such as, but not limited to:
            VIN; vehicle description; premium amount; insured's address;
            garage location; policy endorsements; coverage exclusions;
            other policy-specific information for claims processing.
             */
  policy_details?: PolicyDetail[] | null;
  /**
            Details about the subrogator if health insurance policy + carrier ... if explicitly stated, otherwise null.
             */
  subrogator?: SubrogatorDetail;
  /**
            DEPRECATED: Use Property.owners instead. Owner/manager information is now stored via
            Organization parties linked to the Property subject through Ownership records.
            This field is retained for backward compatibility with existing data but is no longer
            populated for new coverages.
             */
  property_contacts?: PropertyContact[] | null;
  id: string | string;
  status?: CoverageStatus;
  liability_status?: CoverageLiabilityStatus;
  category?: CoverageCategory;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  /**
            List of mentions of this coverage in the matter, if any.
             */
  mentions?: CoverageMentionStubSchema[] | null;
  carrier_v2_id?: (string | string) | null;
  carrier_division_id?: (string | string) | null;
  /** Source of enrichment data for carrier information */
  enrichment_source?: EnrichmentSource;
  /** Metadata about enrichment source (source matter ID, match reason, etc.) */
  enrichment_metadata?: EnrichmentMetadata;
  /** Whether cached data was used for enrichment */
  cache_used?: boolean;
  lor_document_id?: (string | string) | null;
  lor_uploaded_artifact_id?: (string | string) | null;
  lor_uploaded_artifact?: LorUploadedArtifactSummary;
  /** AI-discovered context for opening a claim with citations and confidence scores. */
  open_claim_context?: OpenClaimContextSchema;
  created_by_user_id?: (string | string) | null;
  created_by_user_name?: string | null;
  /** For PROPERTY category: UUID of the linked Property subject. */
  property_id?: (string | string) | null;
};
export type CoverageCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  needs_approval: boolean;
};
export type CustomUserSchema = {
  id: string | string;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  is_firm_admin?: boolean;
  avatar?: string | null;
  signature?: string | null;
};
export type AgentType =
  | "ingest_transcript_task"
  | "provider_finder"
  | "artifact_digest"
  | "treatment_researcher"
  | "treatment_request_automation"
  | "bland_caller"
  | "slack_task_bot"
  | "blob_sorter"
  | "front_app_integration"
  | "task_generator"
  | "coverage_extractor"
  | "intake_converter"
  | "airtable_sync"
  | "system"
  | "backfill_script"
  | "domain_event_update_orchestrator"
  | "loa_detector"
  | "rainmaker"
  | "deliverable_sync"
  | "bpo_agent"
  | "loa_chaser"
  | "external_task_rule_engine"
  | "external_task_bulk_create"
  | "entity_sync"
  | "case_activation";
export type DiscriminatedCoverageCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_created";
  payload: CoverageCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CarrierLinkContext = {
  carrier_id: string | string;
  carrier_name: string;
  division_id?: (string | string) | null;
  division_name?: string | null;
  reasoning?: string | null;
  match_method?: string | null;
};
export type CoverageUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  diff: {
    [key: string]: any;
  };
  previous?: CoverageSchema;
  carrier_link_context?: CarrierLinkContext;
};
export type DiscriminatedCoverageUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_updated";
  payload: CoverageUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CoverageStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  old_status: CoverageStatus;
  new_status: CoverageStatus;
};
export type DiscriminatedCoverageStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_status_changed";
  payload: CoverageStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CoverageCategoryChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  old_category: CoverageCategory;
  new_category: CoverageCategory;
};
export type DiscriminatedCoverageCategoryChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_category_changed";
  payload: CoverageCategoryChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoRDataUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: (string | string) | null;
  coverage_id?: (string | string) | null;
  mapping?: string | null;
  old_value?: string | null;
  new_value?: string | null;
};
export type DiscriminatedLoRDataUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "lor_data_updated";
  payload: LoRDataUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterType =
  | "Motor Vehicle Accident"
  | "Medical Malpractice"
  | "Miscellaneous"
  | "Breach of Employment Contract"
  | "Seller Non-Disclosure"
  | "Premises Liability"
  | "Broker Negligence"
  | "Property Damage"
  | "Dog Bite"
  | "Wrongful Death"
  | "Unknown";
export type MatterStatus =
  | "New Lead"
  | "Intake"
  | "Case Ingestion"
  | "Initial Review"
  | "Case Setup"
  | "Treatment"
  | "Treatment Complete"
  | "Ready for Demand"
  | "Demand Preparation"
  | "Demand Letter"
  | "Litigation"
  | "Complete"
  | "Hidden"
  | "In Progress"
  | "Client Dropped"
  | "To Be Dropped";
export type AddressUpdateModel = {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?:
    | (
        | "AF"
        | "AX"
        | "AL"
        | "DZ"
        | "AS"
        | "AD"
        | "AO"
        | "AI"
        | "AQ"
        | "AG"
        | "AR"
        | "AM"
        | "AW"
        | "AU"
        | "AT"
        | "AZ"
        | "BS"
        | "BH"
        | "BD"
        | "BB"
        | "BY"
        | "BE"
        | "BZ"
        | "BJ"
        | "BM"
        | "BT"
        | "BO"
        | "BQ"
        | "BA"
        | "BW"
        | "BV"
        | "BR"
        | "IO"
        | "BN"
        | "BG"
        | "BF"
        | "BI"
        | "CV"
        | "KH"
        | "CM"
        | "CA"
        | "KY"
        | "CF"
        | "TD"
        | "CL"
        | "CN"
        | "CX"
        | "CC"
        | "CO"
        | "KM"
        | "CG"
        | "CD"
        | "CK"
        | "CR"
        | "CI"
        | "HR"
        | "CU"
        | "CW"
        | "CY"
        | "CZ"
        | "DK"
        | "DJ"
        | "DM"
        | "DO"
        | "EC"
        | "EG"
        | "SV"
        | "GQ"
        | "ER"
        | "EE"
        | "SZ"
        | "ET"
        | "FK"
        | "FO"
        | "FJ"
        | "FI"
        | "FR"
        | "GF"
        | "PF"
        | "TF"
        | "GA"
        | "GM"
        | "GE"
        | "DE"
        | "GH"
        | "GI"
        | "GR"
        | "GL"
        | "GD"
        | "GP"
        | "GU"
        | "GT"
        | "GG"
        | "GN"
        | "GW"
        | "GY"
        | "HT"
        | "HM"
        | "VA"
        | "HN"
        | "HK"
        | "HU"
        | "IS"
        | "IN"
        | "ID"
        | "IR"
        | "IQ"
        | "IE"
        | "IM"
        | "IL"
        | "IT"
        | "JM"
        | "JP"
        | "JE"
        | "JO"
        | "KZ"
        | "KE"
        | "KI"
        | "KP"
        | "KR"
        | "KW"
        | "KG"
        | "LA"
        | "LV"
        | "LB"
        | "LS"
        | "LR"
        | "LY"
        | "LI"
        | "LT"
        | "LU"
        | "MO"
        | "MG"
        | "MW"
        | "MY"
        | "MV"
        | "ML"
        | "MT"
        | "MH"
        | "MQ"
        | "MR"
        | "MU"
        | "YT"
        | "MX"
        | "FM"
        | "MD"
        | "MC"
        | "MN"
        | "ME"
        | "MS"
        | "MA"
        | "MZ"
        | "MM"
        | "NA"
        | "NR"
        | "NP"
        | "NL"
        | "NC"
        | "NZ"
        | "NI"
        | "NE"
        | "NG"
        | "NU"
        | "NF"
        | "MK"
        | "MP"
        | "NO"
        | "OM"
        | "PK"
        | "PW"
        | "PS"
        | "PA"
        | "PG"
        | "PY"
        | "PE"
        | "PH"
        | "PN"
        | "PL"
        | "PT"
        | "PR"
        | "QA"
        | "RE"
        | "RO"
        | "RU"
        | "RW"
        | "BL"
        | "SH"
        | "KN"
        | "LC"
        | "MF"
        | "PM"
        | "VC"
        | "WS"
        | "SM"
        | "ST"
        | "SA"
        | "SN"
        | "RS"
        | "SC"
        | "SL"
        | "SG"
        | "SX"
        | "SK"
        | "SI"
        | "SB"
        | "SO"
        | "ZA"
        | "GS"
        | "SS"
        | "ES"
        | "LK"
        | "SD"
        | "SR"
        | "SJ"
        | "SE"
        | "CH"
        | "SY"
        | "TW"
        | "TJ"
        | "TZ"
        | "TH"
        | "TL"
        | "TG"
        | "TK"
        | "TO"
        | "TT"
        | "TN"
        | "TR"
        | "TM"
        | "TC"
        | "TV"
        | "UG"
        | "UA"
        | "AE"
        | "GB"
        | "US"
        | "UM"
        | "UY"
        | "UZ"
        | "VU"
        | "VE"
        | "VN"
        | "VG"
        | "VI"
        | "WF"
        | "EH"
        | "YE"
        | "ZM"
        | "ZW"
      )
    | null;
};
export type MatterClientPhoneNumberSchema = {
  phone_number: string;
};
export type MatterClientSchema = {
  id?: (string | string) | null;
  name?: string | null;
  email?: string | null;
  date_of_birth?: string | null;
  last_four_of_ssn?: string | null;
  preferred_language?: string | null;
  address?: AddressUpdateModel;
  phone_numbers?: MatterClientPhoneNumberSchema[];
};
export type AltAttachmentSchema = {
  id: string;
  name?: string | null;
  content_type?: string | null;
  size?: number | null;
};
export type MessageIdAndAttachmentSchema = {
  message_id: string;
  attachment: AltAttachmentSchema;
};
export type MatterServices =
  | "Screening & Retainer"
  | "Intake Call"
  | "Case Setup"
  | "Medical Records"
  | "Demand";
export type SimplifiedBillingCode =
  | "records_retrieval"
  | "demand_only"
  | "property_damage"
  | "in_flight"
  | "back_office"
  | "full_pre_lit"
  | "case_setup_only"
  | "litigation_only";
export type TaskConfigSchema = {
  gate_tasks_during_initial_review?: boolean;
};
export type MatterSchema = {
  firm: string | string;
  name?: string | null;
  type?: string | null;
  creator?: (string | string) | null;
  case_manager_id?: (string | string) | null;
  auto_task_assignment?: boolean | null;
  matter_type?: MatterType;
  status?: MatterStatus;
  clients?: MatterClientSchema[];
  front_attachments?: MessageIdAndAttachmentSchema[];
  front_attachment_urls?: string[];
  date_of_loss?: string | null;
  statute_of_limitations_date?: string | null;
  services?: MatterServices[];
  contact_client_directly?: boolean | null;
  city?: string | null;
  state?: string | null;
  representing_attorney?: (string | string) | null;
  external_cms_url?: string | null;
  cms_email?: string | null;
  referral_source_description?: string | null;
  intake_id?: (string | string) | null;
  last_contacted_at_override?: string | null;
  simplified_billing_code?: SimplifiedBillingCode;
  task_config?: TaskConfigSchema;
};
export type MatterRetainerSignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type DiscriminatedMatterRetainerSignedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_retainer_signed";
  payload: MatterRetainerSignedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterRetainerSignedByClientEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type DiscriminatedMatterRetainerSignedByClientEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_retainer_signed_by_client";
  payload: MatterRetainerSignedByClientEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterRetainerSignedByAttorneyEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type DiscriminatedMatterRetainerSignedByAttorneyEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_retainer_signed_by_attorney";
  payload: MatterRetainerSignedByAttorneyEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PoliceReportAttestationStatus =
  | "rejected_verified"
  | "unclear"
  | "inferred"
  | "attested_unverified"
  | "attested_verified"
  | "verified_received";
export type PoliceReportAvailabilityStatus =
  | "verified_ready"
  | "verified_pending"
  | "unknown"
  | "available"
  | "not_available";
export type OfficerDetail = {
  name: string;
  badge_number?: string | null;
  context?: string | null;
};
export type PoliceReportEscalationReason =
  | "cant_verify_attested_report"
  | "verified_ready_but_no_automation_available"
  | "verified_but_unclear_if_ready"
  | "found_on_portal_but_failed_to_retrieve";
export type JurisdictionType =
  | "city"
  | "county"
  | "state"
  | "federal"
  | "tribal"
  | "special";
export type PoliceDepartmentEnrichmentStatus =
  | "not_enriched"
  | "enrichment_in_progress"
  | "enrichment_failed"
  | "enrichment_succeeded";
export type ServiceType =
  | "crash_reports"
  | "foia_equivalent"
  | "criminal_records"
  | "incident_reports"
  | "general_records";
export type PoliceRecordsInquiryMajorVendor =
  | "carfax_crashdocs"
  | "lexis_nexis_buycrash"
  | "independent_portal"
  | "next_request"
  | "gov_qa"
  | "not_a_portal"
  | "txdot_cris";
export type AddressSchema = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province_or_state?: string | null;
  postal_or_zip?: string | null;
  country_code?: string | null;
};
export type MailRequirement =
  | "CLIENT_PHOTO_ID_COPY"
  | "FIRM_LETTERHEAD"
  | "PROOF_OF_REPRESENTATION"
  | "COMPLETED_REQUEST_FORM"
  | "NOTARIZED_REQUEST_FORM";
export type FeeComponent = {
  /** The amount of the fee in cents. */
  amount_cents: number;
  /** One line description of the component of the total fee backed by research and assumptions.
    ex:
    - "Upper bound estimate of 20 pages at $0.25/page as specified by ____"
    - "Estimating up to 30min of labor at stated rate of $20/hour as specified by ____"
    - "Base certified crash report fee $8.44 as specified by ____"
    - "Added 7% for tax because site mentions it's not included in provided info" */
  description: string;
};
export type RecordsFeeEstimate = {
  /** An estimated fee for requesting a copy of a police report.
    Depending on the department, sometimes this is called a "crash report", "accident report",
    "incident report", or "police report".
    
    Rule: IF more than one report option is available, bias to the more expensive, or certified option.
    If fees are not defined for the department, fall back to county or state guidance on fees. */
  estimated_fee_cents?: number | null;
  /** All the components that led to the estimated fee */
  components?: FeeComponent[] | null;
  /** Full verbatim fee details including:
    - Exact language from official source
    - Conditions, alternatives, or special cases
    - Statutory citations (e.g., "per RSMo §610.026")
    - Payment instructions
    ALWAYS populate this when ANY fee information is available. */
  notes?: string | null;
  /** A 0-1 score indicating the confidence. */
  confidence_score?: number | null;
  /** 2-3 sentences explaining the confidence score. */
  reasoning?: string | null;
};
export type CheckMailingInstructions = {
  /** Verifiable instructions on how to request a police report from the agency by mail. */
  instructions?: string;
  /** The mailing address to send the request to. */
  address: AddressSchema;
  /** The best, single name of the entity to make the check payable to. (Payee)
    Do not place anything except for a single, bank-ready name here. */
  payable_to?: string | null;
  /** Any specific text required to be written on the check memo line, if explicitly mentioned, otherwise null.
    Example: "Police Report Request" or "Report #[REPORT_NUMBER]" or "Accident Report Fee"
    available variables: "[REPORT_NUMBER], [CLIENT_NAME], [DATE_OF_LOSS]" */
  memo_instructions?: string | null;
  /** The number of days after incident date to wait before requesting
    the records by mail, if explicitly specified, otherwise null. */
  wait_before_requesting_days?: number | null;
  /** Any department-specified, or state-specified, number of days to expect for processing, if explicitly stated, otherwise null. */
  estimated_processing_days?: number | null;
  /** Any items that are explicitly REQUIRED for requesting a police report by mail, if described, otherwise null. */
  required_items?: MailRequirement[] | null;
  /** Any special requirements for requesting a police report by mail, if described, otherwise null. */
  special_requirements?: string | null;
  /** The estimated set of fees to request a copy of a police report by mail.
    
    - (for any question of the report type being sought): assume we want the most detailed version of the report, sometimes this is "crash report", "accident report", "incident report", "police report", bias to the more expensive of the four
    - (for any page-based fee structure) assume a 20 page incident report
    - (for any requestor-based fee differences) assume legal representative of involved party
    - (for request-method based pricing) assume check + mail but bias to more expensive option
    - (for any charge for a missing report number) assume we do not have the report number
    - (for any charges related to labor) assume 30 minutes of labor
    - (for any charges related to certified copies) assume we are paying for the most certified copy */
  fee_structure?: RecordsFeeEstimate;
  /** Web pages that were used to gather the information. */
  citations?: WebSource[] | null;
};
export type DepartmentForm = {
  /** Concise title for the Form */
  title: string;
  /** Concise sentence description of the Form and its purpose. */
  description?: string | null;
  /** URL of the Form file */
  file_url: string;
};
export type ReadDepartmentRecordsChannel = {
  /** A concise title for the channel - e.g. "FOIA Portal", "Records Department Phone" */
  title?: string | null;
  /** Single sentence description of the channel and its purpose. */
  description?: string | null;
  /** Tags for record types that can be serviced by this channel.
    e.g. ["crash_reports", "foia_equivalent", "general_records"] */
  services_provided?: ServiceType[] | null;
  /** The type of channel: mail, phone, email, online_portal, fax */
  channel_type?: string | null;
  /** Web sources/citations for this channel information */
  citations?: WebSource[] | null;
  /** Concise instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.' */
  instructions: string;
  handle: ContactHandleSchema;
  fee_amount?: number | null;
  major_vendor?: PoliceRecordsInquiryMajorVendor;
  /** An explicitly provided link to a pdf, docx, or other static file form that the department publishes and requires
    inquirers to fill out for making police report requests. Not every PD may ask for inquirers to provide a formal request,
    but if they do, we should capture them here - otherwise null. */
  required_document_url?: WebSource;
  mailing_instructions?: CheckMailingInstructions;
  /** Department-provided forms associated with this channel */
  forms?: DepartmentForm[] | null;
  /** Portal URL if this is an online portal channel */
  portal_url?: string | null;
  /** Portal domain if this is an online portal channel */
  portal_domain?: string | null;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department_id: string | string;
  required_document_file_url?: string | null;
};
export type TimeZone = "PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST";
export type ReadPoliceDepartment = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  records_channels?: ReadDepartmentRecordsChannel[];
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
  id: string | string;
  created_at: string;
  modified_at: string;
};
export type ReadPoliceReport = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  /** Internal ID of the associated police department - not set during extraction */
  police_department_id?: (string | string) | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  matter_id: string | string;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department: ReadPoliceDepartment;
};
export type PoliceReportCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  needs_approval: boolean;
};
export type DiscriminatedPoliceReportCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "police_report_created";
  payload: PoliceReportCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PoliceReportUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  diff: {
    [key: string]: any;
  };
  previous?: ReadPoliceReport;
};
export type DiscriminatedPoliceReportUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "police_report_updated";
  payload: PoliceReportUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PoliceReportStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  old_report_status: PoliceReportAttestationStatus;
  new_report_status: PoliceReportAttestationStatus;
};
export type DiscriminatedPoliceReportStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "police_report_status_changed";
  payload: PoliceReportStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskGroups =
  | "Intake"
  | "Police Report & Investigation"
  | "1P Insurance"
  | "3P Insurance"
  | "Medical Records"
  | "Treatment"
  | "Demand Letter"
  | "Liens"
  | "Miscellaneous"
  | "Additional Insurance"
  | "Initial Medical Records";
export type TaskType =
  | "Medical Records"
  | "Medical Bills"
  | "LOR Acknowledgement"
  | "Send LOR"
  | "Open Claim"
  | "Final Lien"
  | "Lien Acknowledgement"
  | "Police Report"
  | "Police Report Escalation"
  | "Freedom of Information Act"
  | "Incident Report"
  | "Open File"
  | "Find Health Insurance Coverage"
  | "Locate Report Source"
  | "Ascertain Subrogator"
  | "Finalize Demand"
  | "Draft Demand"
  | "Demand Readiness Review"
  | "Declaration Page"
  | "Health Insurance Card"
  | "Treatment Check-In"
  | "Treatment Barrier"
  | "HIPAA Directive"
  | "Retainer Agreement"
  | "Conduct Intake"
  | "Conduct Initial Review"
  | "Welcome Call"
  | "Other"
  | "All"
  | "Review Found Coverage"
  | "Review Found Provider"
  | "Update External CMS"
  | "Find 1P Coverage"
  | "Find 3P Coverage"
  | "Client Communications"
  | "Managing Paralegal Asks"
  | "Manage MedPay"
  | "Send MedPay Letter"
  | "Get Liability Limits"
  | "Request Updated PIP Ledger"
  | "Send Evidence Preservation Letter"
  | "Document Collection"
  | "Review 1P Benefits"
  | "PIP/MedPay Application"
  | "Check for PIP Ledger"
  | "IME Required"
  | "UM/UIM Indicate Stack vs. No Stack"
  | "Generate No Medpay Letter"
  | "Provide PIP Info to Medical Provider"
  | "Liability Other"
  | "1P Insurance Other"
  | "3P Insurance Other"
  | "Liens Other"
  | "Treatment Other"
  | "Medical Records Other"
  | "Demand Letter Other"
  | "Confirm Incident Location"
  | "Research Property & Owners";
export type TaskExternalCmsUpdateStatus = "NOT_UPDATED" | "UPDATED";
export type TaskTemplateType =
  | "intake_conduct_intake"
  | "intake_conduct_initial_review"
  | "intake_signed_retainer"
  | "intake_signed_hipaa"
  | "intake_health_insurance_card"
  | "intake_declaration_page"
  | "intake_update_external_cms"
  | "intake_welcome_call"
  | "initial_review_client_drivers_license"
  | "initial_review_defendant_drivers_license"
  | "initial_review_client_auto_insurance"
  | "initial_review_request_supplemental"
  | "police_locate_police_report"
  | "police_retrieve_police_report"
  | "police_confirm_found_report"
  | "police_eliminated_pd"
  | "police_provide_missing_info"
  | "police_not_applicable"
  | "police_researching"
  | "police_unverified"
  | "police_verified"
  | "police_manual_intervention_required"
  | "police_confirm_no_report_exists"
  | "police_report_v2"
  | "police_foia_request"
  | "police_incident_report"
  | "insurance_1p_open_1p_claim"
  | "insurance_1p_send_1p_lor"
  | "insurance_1p_receive_1p_acknowledgement"
  | "insurance_1p_get_liability_limits"
  | "insurance_1p_declaration_page"
  | "insurance_3p_open_3p_claim"
  | "insurance_3p_send_3p_lor"
  | "insurance_3p_receive_3p_acknowledgement"
  | "insurance_3p_get_liability_limits"
  | "liens_open_file"
  | "liens_final_lien"
  | "liens_receive_lien_acknowledgement"
  | "liens_find_health_insurance_coverage"
  | "demands_draft_demand"
  | "demands_finalize_demand"
  | "demands_demand_readiness_review"
  | "treatment_treatment_check_in"
  | "treatment_resolve_barrier"
  | "treatment_requests_submit_request"
  | "medical_provider_review_found_provider"
  | "medical_provider_request_medical_records"
  | "treatment_record_request"
  | "medical_provider_request_medical_bills"
  | "medical_provider_request_affidavit"
  | "coverage_review_found_coverage"
  | "coverage_find_1p_coverage"
  | "coverage_find_3p_coverage"
  | "coverage_review_1p_benefits"
  | "coverage_pip_medpay_application"
  | "coverage_check_for_pip_ledger"
  | "coverage_ime_required"
  | "coverage_generate_no_medpay_letter"
  | "coverage_manage_medpay"
  | "coverage_provide_pip_info_to_provider"
  | "premises_confirm_incident_location"
  | "premises_research_property_owners"
  | "premises_send_lor"
  | "escalation_case_escalation";
export type TaskSchema = {
  id: string | string;
  matter_id?: (string | string) | null;
  name: string;
  description: string;
  group: TaskGroups;
  type: TaskType;
  due_date?: string | null;
  medical_note_id?: (string | string) | null;
  coverage_id?: (string | string) | null;
  police_report_id?: (string | string) | null;
  treatment_record_request_id?: (string | string) | null;
  external_cms_update_status?: TaskExternalCmsUpdateStatus;
  external_cms_update_reason?: string | null;
  assignee_id?: (string | string) | null;
  created_by_id?: (string | string) | null;
  template_type?: TaskTemplateType;
};
export type TaskCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
};
export type DiscriminatedTaskCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_created";
  payload: TaskCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskStatus =
  | "Pending"
  | "Blocked"
  | "In Progress"
  | "Completed"
  | "Not Applicable"
  | "Closed";
export type TaskStatusSchema = {
  id: string | string;
  name: string;
  canonical_status: TaskStatus;
  description?: string | null;
};
export type TaskStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: TaskStatusSchema;
  new?: TaskStatusSchema;
};
export type DiscriminatedTaskStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_status_changed";
  payload: TaskStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskAssignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: CustomUserSchema;
  new?: CustomUserSchema;
};
export type DiscriminatedTaskAssignedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_assigned";
  payload: TaskAssignedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskDueDateChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: string | null;
  new?: string | null;
};
export type DiscriminatedTaskDueDateChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_due_date_changed";
  payload: TaskDueDateChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskPriorityChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: number | null;
  new?: number | null;
};
export type DiscriminatedTaskPriorityChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_priority_changed";
  payload: TaskPriorityChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskUpdateType = "comment" | "activity";
export type TaskUpdateVisibility = "Private" | "Public";
export type TaskUpdateSchema = {
  id: string | string;
  task_id: string | string;
  update: string;
  update_type: TaskUpdateType;
  visibility: TaskUpdateVisibility;
  user?: CustomUserSchema;
  agent?: AgentType;
  agent_tags?: string[];
  correlation_id?: (string | string) | null;
  created_at: string;
  modified_at: string;
};
export type TaskUpdateCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task_update: TaskUpdateSchema;
};
export type DiscriminatedTaskUpdateCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_update_created";
  payload: TaskUpdateCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type UpdateTaskUpdateSchema = {
  /** The update to the task. This is a free-form text field that can be used to add any relevant information. */
  update?: string | null;
  /** Whether the update is visible to the customer, or internal to the operations staff. */
  visibility?: TaskUpdateVisibility;
  /** A list of Blob ids attached to the update. */
  attachments?: (string | string)[] | null;
  /** The user id of the user who created the update. If not provided, the update will be attributed to the system. */
  user_id?: (string | string) | null;
  agent?: AgentType;
  /** The type of update. This is used to determine the type of update to create. */
  update_type?: TaskUpdateType;
};
export type TaskUpdateUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task_update: TaskUpdateSchema;
  diff: UpdateTaskUpdateSchema;
  previous?: TaskUpdateSchema;
};
export type DiscriminatedTaskUpdateUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_update_updated";
  payload: TaskUpdateUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskExternalCmsUpdateStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old: TaskExternalCmsUpdateStatus;
  new: TaskExternalCmsUpdateStatus;
};
export type DiscriminatedTaskExternalCmsUpdateStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_external_cms_update_status_changed";
  payload: TaskExternalCmsUpdateStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderStatus = "NEEDS_APPROVAL" | "APPROVED" | "REJECTED";
export type ProviderTreatmentStatus =
  | "TREATING"
  | "COMPLETED"
  | "DEVIATING"
  | "UNKNOWN"
  | "REFERRED_TO";
export type ProviderType = "ORGANIZATION" | "PRACTITIONER";
export type ProviderSearchResult = {
  /** Name of the practitioner (e.g. 'Dr. John Smith') if explicitly stated, null otherwise. */
  practitioner_name?: string | null;
  /** Common, recognizable name of the healthcare organization (e.g., 'UC San Diego Health', 'Main Street Clinic'). EXCLUDE administrative department names (e.g., 'Medical Records', 'Billing'); these belong in handles. Align with original mention's generality unless search strongly indicates a specific, distinct facility. */
  organization_name?: string | null;
  /** The address of the provider. */
  address?: string | null;
  /** The BEST (at MOST 2 handles per type) contact methods for records/billing retrieval. Do not include scheduling phone numbers, or other patient care oriented phone numbers. */
  handles: ContactHandleSchema[];
  /** A very brief (1-2 sentences) note for paralegal context. */
  internal_note?: string | null;
};
export type EncounterSchema = {
  /** The unique identifier for the encounter */
  id: string;
  /** The date of service for the encounter in ISO format (YYYY-MM-DD), if explicitly stated, null otherwise */
  date?: string | null;
  /** list of short phrases describing care provided in the encounter as mentioned in the artifact, like 'physical therapy', 'labs', 'imaging', etc. if explicitly stated, null otherwise */
  services?: string[];
  /** A very concise description of the encounter, as a one liner that might be used in a medical chronology for the case. Don't repeat the date of service in the description . Explicitly mention any ambiguities about the discovery that merit further investigation (i.e date of service not yet known, etc.) */
  description: string;
};
export type TreatmentFrequency =
  | "DAILY"
  | "WEEKLY"
  | "BI_WEEKLY"
  | "MONTHLY"
  | "AS_NEEDED"
  | "OTHER";
export type TreatmentProgress = "IMPROVING" | "STAGNANT" | "REGRESSING";
export type TreatmentCareLevel =
  | "EMERGENCY_CARE"
  | "CONSERVATIVE_CARE"
  | "DIAGNOSTIC_TESTING"
  | "SPECIALIST_CARE"
  | "SURGICAL_CARE";
export type MedicalProviderSchema = {
  id: string;
  matter_id: string;
  provider_contact_ref_id?: string | null;
  provider_service_ref_id?: string | null;
  name: string;
  status?: ProviderStatus;
  dates_of_service?: string | null;
  treatment?: string | null;
  address?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  still_treating?: string | null;
  treatment_status?: ProviderTreatmentStatus;
  treatment_status_occurred_at?: string | null;
  date_of_records_request?: string | null;
  provider_type?: ProviderType;
  practitioner_names?: string[] | null;
  description?: string | null;
  handles: ContactHandleSchema[];
  inference_reasoning?: string | null;
  fallback_leads?: ProviderSearchResult[] | null;
  encounters?: EncounterSchema[] | null;
  pain_level?: number | null;
  treatment_notes?: string | null;
  treatment_frequency?: TreatmentFrequency;
  treatment_progress?: TreatmentProgress;
  care_level?: TreatmentCareLevel;
};
export type ProviderCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  needs_approval: boolean;
};
export type DiscriminatedProviderCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_created";
  payload: ProviderCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  old_status: ProviderStatus;
  new_status: ProviderStatus;
};
export type DiscriminatedProviderStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_status_changed";
  payload: ProviderStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderTreatmentStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  old_treatment_status: ProviderTreatmentStatus;
  new_treatment_status: ProviderTreatmentStatus;
};
export type DiscriminatedProviderTreatmentStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_treatment_status_changed";
  payload: ProviderTreatmentStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 2;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderCuratedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  action: string;
  diff?: {
    [key: string]: any;
  } | null;
};
export type DiscriminatedProviderCuratedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_curated";
  payload: ProviderCuratedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PortalSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a medical records request portal (MRO, Smartrequest, Chartswap) */
  channel_type?: "PORTAL";
  /** The uuid of the portal. */
  portal_id: string;
};
export type FaxContactHandle = {
  /** A contact handle for a fax number */
  handle_type?: "FAX";
  /** Should contain an E.164 formatted fax number. */
  value: string;
};
export type FaxSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a fax number */
  channel_type?: "FAX";
  /** The handle of the submission channel. E.g. 'fax number' as well as the value */
  handle: FaxContactHandle;
};
export type EmailContactHandle = {
  /** A contact handle for an email address */
  handle_type?: "EMAIL";
  /** Should contain an email address. */
  value: string;
};
export type EmailSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through an email address */
  channel_type?: "EMAIL";
  /** The handle of the submission channel. E.g. 'email address' as well as the value */
  handle: EmailContactHandle;
};
export type MailingAddressContactHandle = {
  /** A contact handle for a mailing address */
  handle_type?: "MAILING_ADDRESS";
  /** The recipient of the mailing address. */
  recipient: string;
  /** The first line of the mailing address. */
  address_line_1: string;
  /** The second line of the mailing address. */
  address_line_2?: string | null;
  /** The city of the mailing address. */
  city: string;
  /** The state of the mailing address. */
  state: string;
  /** The zip code of the mailing address. */
  zip_code: string;
  /** The country of the mailing address. */
  country: string;
};
export type MailingAddressSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a mailing address */
  channel_type?: "MAILING_ADDRESS";
  /** The handle of the submission channel. E.g. 'mailing address' as well as the value */
  handle: MailingAddressContactHandle;
};
export type WebsiteSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a website */
  channel_type?: "WEBSITE";
  /** The handle of the submission channel. E.g. 'website' as well as the value */
  handle: string;
  /** The url of the website of the submission channel. */
  website: string;
};
export type TreatmentRecordTypes =
  | "Bills"
  | "Records"
  | "Facility Records"
  | "Facility Bills"
  | "Radiology"
  | "ER Physician Bills"
  | "Other"
  | "Unknown";
export type SubmissionMethod = {
  /** The method by which we can submit records to the provider */
  submission_method:
    | PortalSubmissionChannel
    | FaxSubmissionChannel
    | EmailSubmissionChannel
    | MailingAddressSubmissionChannel
    | WebsiteSubmissionChannel;
  /** A short and concise explanation of why this submission method is a good candidate for requesting records. Write it as you would explain it to a regular person. */
  reason: string;
  /** A score between 0 and 1 indicating the confidence in the submission method  */
  confidence: number;
  /** The scope (records we can expect to receive) for the submission method */
  scope?: TreatmentRecordTypes[] | null;
  /** Any special instructions for submitting records to the provider */
  submission_instructions?: string | null;
};
export type TreatmentRequestResearchCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider_id: string | string;
  submission_methods: SubmissionMethod[];
  selected_submission_methods: SubmissionMethod[];
};
export type DiscriminatedTreatmentRequestResearchCompletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_research_completed";
  payload: TreatmentRequestResearchCompletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TreatmentRecordRequestActor = "user" | "agent";
export type TreatmentRecordRequestStatus =
  | "not_ready"
  | "pending"
  | "request_submitted"
  | "request_received"
  | "awaiting_invoice"
  | "pending_payment"
  | "records_pending"
  | "records_delivered"
  | "records_ingested"
  | "rejected"
  | "cancelled"
  | "auto_retried";
export type Blockers =
  | "HIPAA_PACKAGE_NOT_READY"
  | "CLIENT_INFORMATION_NOT_ENTERED"
  | "PROVIDER_CONTACT_INFORMATION_NOT_ENTERED"
  | "PORTAL_REQUEST_ID_REQUIRED"
  | "NO_REQUEST_SENT_AT"
  | "VOICE_AI_INFORMATION_NOT_ENTERED"
  | "FOLLOWUP_HIPAA_PACKAGE_NOT_SUBMITTED"
  | "FOLLOWUP_CHANNEL_NOT_CONFIGURED"
  | "FOLLOWUP_CHANNEL_UNSUPPORTED"
  | "FOLLOWUP_FAX_NUMBER_MISSING"
  | "FOLLOWUP_EMAIL_ADDRESS_MISSING";
export type TreatmentRecordRequestAutomationBlockedSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "blocked";
  blocked_by?: Blockers[];
};
export type TreatmentRecordRequestAutomationFailedSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "failed";
};
export type TreatmentRecordRequestAutomationIdleSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "idle";
};
export type TreatmentRecordRequestAutomationRunningSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "running";
};
export type TreatmentRecordRequestAutomationSleepingSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "sleeping";
  sleep_until: string;
};
export type PatientInformation = {
  client_name: string;
  client_date_of_birth: string;
};
export type TreatmentRecordRequestChannelType =
  | "portal"
  | "human"
  | "email"
  | "fax"
  | "mail"
  | "phone";
export type RequestDetails = {
  request_method: TreatmentRecordRequestChannelType;
  request_sent_at: string;
  request_method_target: string;
  contact_number: string;
};
export type RequesteeInformation = {
  requestee_name: string;
};
export type VoiceAiCallArgs = {
  patient_information: PatientInformation;
  request_details: RequestDetails;
  requestee_information: RequesteeInformation;
  /** Human-readable description of the requested records (e.g., Medical Records, Bills, Radiology). */
  request_type_label?: string | null;
  /** Skip business-hours scheduling and execute the call immediately. Used for manual/bulk calls that should run ASAP. */
  skip_scheduling_delay?: boolean;
};
export type AlternateContactHandleType = "fax" | "email" | "mailing_address";
export type AlternateContactSchema = {
  /** Type of contact method (fax or mailing address) */
  handle_type: AlternateContactHandleType;
  /** The actual contact value (fax number or mailing address) */
  value: string;
  /** Optional human-readable label or description */
  label?: string | null;
  /** Optional confidence score from AI extraction (0-1) */
  confidence?: number | null;
};
export type ContactDiscoveryCallSummarySchema = {
  /** Short text describing what the facility told us. */
  call_summary: string;
  /** List of submission channels (portal, fax, email) mentioned during the call. */
  submission_methods?: string[];
  /** Documents or prerequisites (HIPAA, ID, invoices) called out by the facility. */
  required_documents?: string[];
  /** Structured alternates (fax numbers, mailing addresses) discovered during the call. */
  alternate_contacts?: AlternateContactSchema[];
  /** Optional free-form notes for additional context. */
  notes?: string | null;
};
export type TransmissionStrategyConfig = {
  enabled?: boolean;
  channel?: TreatmentRecordRequestChannelType;
  interval_days?: number;
  max_attempts?: number;
  stop_on_voice_ai_contact?: boolean;
  stop_on_status_change?: boolean;
  last_transmission_at?: string | null;
  next_transmission_at?: string | null;
  total_transmissions_sent?: number;
  failed_attempts?: number;
};
export type TreatmentRecordRequestMetadataSchema = {
  slack_message_id?: string | null;
  disable_automations?: boolean;
  automation_state_v2?:
    | (
        | ({
            status: "blocked";
          } & TreatmentRecordRequestAutomationBlockedSchema)
        | ({
            status: "failed";
          } & TreatmentRecordRequestAutomationFailedSchema)
        | ({
            status: "idle";
          } & TreatmentRecordRequestAutomationIdleSchema)
        | ({
            status: "running";
          } & TreatmentRecordRequestAutomationRunningSchema)
        | ({
            status: "sleeping";
          } & TreatmentRecordRequestAutomationSleepingSchema)
      )
    | null;
  ad_hoc_voice_ai_args?: VoiceAiCallArgs;
  front_conversation_id?: string | null;
  front_conversation_url?: string | null;
  email_sent_at?: string | null;
  /** Number of days between voice AI follow-up calls */
  voice_ai_check_interval_days?: number;
  next_scheduled_call?: string | null;
  request_submitted_at?: string | null;
  initial_contact_method_call_completed_at?: string | null;
  voice_ai_call_summary?: string | null;
  voice_ai_discovered_alternates?: {
    [key: string]: any;
  }[];
  voice_ai_call_id?: string | null;
  superseded_by_request_id?: string | null;
  supersession_reason?: string | null;
  last_notification_category?: string | null;
  last_notification_subcategory?: string | null;
  last_call_key_information?: {
    [key: string]: any;
  };
  last_voice_ai_call_at?: string | null;
  last_voice_ai_call_summary?: string | null;
  dynamic_retry_delay_hours?: number | null;
  contact_discovery_summary?: ContactDiscoveryCallSummarySchema;
  contact_discovery_last_updated_at?: string | null;
  hipaa_package_locked?: boolean;
  hipaa_package_locked_at?: string | null;
  hipaa_package_submission_channel?: TreatmentRecordRequestChannelType;
  hipaa_package_submission_workflow_id?: string | null;
  alternate_contact?: AlternateContactSchema;
  forked_from_request_id?: string | null;
  transmission_strategy?: TransmissionStrategyConfig;
};
export type TreatmentRecordRequestPortalType =
  | "smart_request"
  | "mro"
  | "chartswap";
export type PortalRecordType = {
  id: string | string;
  created_at: string;
  modified_at: string;
  original_name?: string | null;
  record_type: TreatmentRecordTypes;
};
export type PortalProvider = {
  id: string | string;
  created_at: string;
  modified_at: string;
  name: string;
  portal: TreatmentRecordRequestPortalType;
  active?: boolean;
  external_id?: string | null;
  site?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  fax?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  record_types?: PortalRecordType[] | null;
};
export type TreatmentRecordRequestChannelSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  type: TreatmentRecordRequestChannelType;
  portal_provider_id?: (string | string) | null;
  phone_number?: string | null;
  address?: string | null;
  email?: string | null;
  fax?: string | null;
  instructions?: string | null;
  portal_record_request_id?: string | null;
  provider_contact_unit_handle_id?: (string | string) | null;
  provider_directory_contact_point_id?: (string | string) | null;
  portal_provider?: PortalProvider;
};
export type FaxTransmissionStatusSchema = {
  id?: (string | string) | null;
  status?: ("pending" | "sent" | "delivered" | "failed") | null;
  sent_at?: string | null;
  delivered_at?: string | null;
  failed_at?: string | null;
  transmission_id?: string | null;
  error_message?: string | null;
};
export type EmailTransmissionStatusSchema = {
  sent_at?: string | null;
  front_conversation_id?: string | null;
  front_conversation_url?: string | null;
  recipient_email?: string | null;
};
export type TreatmentRecordRequestSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  provider_id: string | string;
  actor?: TreatmentRecordRequestActor;
  is_certified?: boolean;
  is_urgent?: boolean;
  provided_records?: TreatmentRecordTypes[];
  status?: TreatmentRecordRequestStatus;
  metadata?: TreatmentRecordRequestMetadataSchema;
  channel?: TreatmentRecordRequestChannelSchema;
  fax_transmission_status?: FaxTransmissionStatusSchema;
  email_transmission_status?: EmailTransmissionStatusSchema;
  task_id?: (string | string) | null;
};
export type TreatmentRequestCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
};
export type DiscriminatedTreatmentRequestCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_created";
  payload: TreatmentRequestCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type UpdateTreatmentRecordRequestSchema = {
  request_id: string | string;
  actor?: TreatmentRecordRequestActor;
  is_certified?: boolean | null;
  is_urgent?: boolean | null;
  provided_records?: TreatmentRecordTypes[] | null;
  status?: TreatmentRecordRequestStatus;
  metadata?: TreatmentRecordRequestMetadataSchema;
};
export type TreatmentRequestUpdatedEventV1 = {
  /** PK of the row that changed */
  target_id: string | string;
  diff: UpdateTreatmentRecordRequestSchema;
};
export type DiscriminatedTreatmentRequestUpdatedEventV1 = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_updated";
  payload: TreatmentRequestUpdatedEventV1;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TreatmentRequestUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
  diff: UpdateTreatmentRecordRequestSchema;
  previous: TreatmentRecordRequestSchema;
};
export type DiscriminatedTreatmentRequestUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_updated";
  payload: TreatmentRequestUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 2;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type Reason =
  | "treatment_completed"
  | "request_submitted_via_portal"
  | "request_submitted_via_mail"
  | "request_submitted_via_fax"
  | "request_submitted_via_email"
  | "request_updated_via_voice_ai"
  | "request_updated_via_portal"
  | "invoice_paid_via_portal"
  | "records_ingested_via_portal"
  | "manual";
export type TreatmentRequestStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
  old_status: TreatmentRecordRequestStatus;
  new_status: TreatmentRecordRequestStatus;
  reason?: Reason;
};
export type DiscriminatedTreatmentRequestStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_status_changed";
  payload: TreatmentRequestStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaOutreachStatus =
  | "running"
  | "paused"
  | "completed_loa_received"
  | "completed_rejected"
  | "stopped_manually"
  | "monthly_cadence";
export type LoaOutreachStepType =
  | "auto_email"
  | "auto_fax"
  | "manual_call"
  | "manual_email"
  | "supervisor_escalation";
export type LoaOutreachCampaignSchema = {
  id: string | string;
  matter_id: string | string;
  coverage_id: string | string;
  workflow_id: string;
  status: LoaOutreachStatus;
  current_step_index: number;
  current_step_label: string;
  next_action_date?: string | null;
  next_action_type?: LoaOutreachStepType;
  next_action_label?: string;
  last_contact_date?: string | null;
  last_contact_type?: LoaOutreachStepType;
  total_contact_count?: number;
  email_thread_id?: string;
  stopped_reason?: string | null;
  created_at: string;
  modified_at: string;
};
export type LoaChaserCampaignStartedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  starting_step: string;
  email_thread_id: string;
};
export type DiscriminatedLoaChaserCampaignStartedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_started";
  payload: LoaChaserCampaignStartedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserCampaignPausedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
};
export type DiscriminatedLoaChaserCampaignPausedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_paused";
  payload: LoaChaserCampaignPausedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserCampaignResumedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
};
export type DiscriminatedLoaChaserCampaignResumedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_resumed";
  payload: LoaChaserCampaignResumedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserCampaignStoppedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  reason?: string | null;
};
export type DiscriminatedLoaChaserCampaignStoppedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_stopped";
  payload: LoaChaserCampaignStoppedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserCampaignCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  outcome: LoaOutreachStatus;
};
export type DiscriminatedLoaChaserCampaignCompletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_completed";
  payload: LoaChaserCampaignCompletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserStepExecutedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  step_id: string;
  step_label: string;
  step_type: LoaOutreachStepType;
  was_auto?: boolean;
};
export type DiscriminatedLoaChaserStepExecutedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_step_executed";
  payload: LoaChaserStepExecutedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserStepChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  previous_step_id: string;
  previous_step_label: string;
  new_step_id: string;
  new_step_label: string;
  /** 'skip' (forward) or 'reset' (backward) */
  direction: string;
};
export type DiscriminatedLoaChaserStepChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_step_changed";
  payload: LoaChaserStepChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserManualActivityLoggedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  activity_type: LoaOutreachStepType;
  notes: string;
  executed_at: string;
};
export type DiscriminatedLoaChaserManualActivityLoggedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_manual_activity_logged";
  payload: LoaChaserManualActivityLoggedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserScheduleRealignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  previous_next_step: string;
  previous_due_date: string;
  new_next_step: string;
  new_due_date: string;
  trigger: string;
};
export type DiscriminatedLoaChaserScheduleRealignedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_schedule_realigned";
  payload: LoaChaserScheduleRealignedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserSnoozedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  snooze_days: number;
  previous_due_date: string;
  new_due_date: string;
};
export type DiscriminatedLoaChaserSnoozedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_snoozed";
  payload: LoaChaserSnoozedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskActivity = {
  event:
    | DiscriminatedCoverageCreatedEvent
    | DiscriminatedCoverageUpdatedEvent
    | DiscriminatedCoverageStatusChangedEvent
    | DiscriminatedCoverageCategoryChangedEvent
    | DiscriminatedLoRDataUpdatedEvent
    | DiscriminatedMatterRetainerSignedEvent
    | DiscriminatedMatterRetainerSignedByClientEvent
    | DiscriminatedMatterRetainerSignedByAttorneyEvent
    | DiscriminatedPoliceReportCreatedEvent
    | DiscriminatedPoliceReportUpdatedEvent
    | DiscriminatedPoliceReportStatusChangedEvent
    | DiscriminatedTaskCreatedEvent
    | DiscriminatedTaskStatusChangedEvent
    | DiscriminatedTaskAssignedEvent
    | DiscriminatedTaskDueDateChangedEvent
    | DiscriminatedTaskPriorityChangedEvent
    | DiscriminatedTaskUpdateCreatedEvent
    | DiscriminatedTaskUpdateUpdatedEvent
    | DiscriminatedTaskExternalCmsUpdateStatusChangedEvent
    | DiscriminatedProviderCreatedEvent
    | DiscriminatedProviderStatusChangedEvent
    | DiscriminatedProviderTreatmentStatusChangedEvent
    | DiscriminatedProviderCuratedEvent
    | DiscriminatedTreatmentRequestResearchCompletedEvent
    | DiscriminatedTreatmentRequestCreatedEvent
    | DiscriminatedTreatmentRequestUpdatedEventV1
    | DiscriminatedTreatmentRequestUpdatedEvent
    | DiscriminatedTreatmentRequestStatusChangedEvent
    | DiscriminatedLoaChaserCampaignStartedEvent
    | DiscriminatedLoaChaserCampaignPausedEvent
    | DiscriminatedLoaChaserCampaignResumedEvent
    | DiscriminatedLoaChaserCampaignStoppedEvent
    | DiscriminatedLoaChaserCampaignCompletedEvent
    | DiscriminatedLoaChaserStepExecutedEvent
    | DiscriminatedLoaChaserStepChangedEvent
    | DiscriminatedLoaChaserManualActivityLoggedEvent
    | DiscriminatedLoaChaserScheduleRealignedEvent
    | DiscriminatedLoaChaserSnoozedEvent;
};
export const { useTaskActivityListQuery } = injectedRtkApi;
